import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSport, SPORT_META } from "@/context/SportContext";
import { Save, User, Upload, FileText, Trash2, Lock, Download, AlertTriangle, ShieldCheck, Clock, Users, UserPlus, X, Coffee, Eye, EyeOff, Layout, Check } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  ok:        { color: "#CCFF00", icon: ShieldCheck, label: "Valid" },
  warning:   { color: "#FF9F1C", icon: Clock,       label: "Expiring soon" },
  critical:  { color: "#FF4500", icon: AlertTriangle, label: "Expires this week" },
  lapsed:    { color: "#FF4500", icon: AlertTriangle, label: "Lapsed" },
  no_expiry: { color: "#A3A3A3", icon: ShieldCheck, label: "No expiry on file" },
};

export default function CoachProfile() {
  const { user } = useAuth();
  const { enabledSports } = useSport();
  const [profile, setProfile] = useState({
    coach_sports: ["pickleball"], headline: "", bio: "", venues: [], hourly_rate: 20,
    pricing: { group: 12, performance: 60, one_on_one: 40 },
  });
  const [saving, setSaving] = useState(false);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [creds, setCreds] = useState(null);
  const [regions, setRegions] = useState([]);  // all curated regions
  const [myRegions, setMyRegions] = useState([]);  // ids the coach is approved for
  const [pendingReqs, setPendingReqs] = useState([]);  // pending region requests
  const [mySuggestions, setMySuggestions] = useState([]);  // coach's own region proposals
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestForm, setSuggestForm] = useState({ suggested_label: "", parent_region: "", centre_postcode: "", notes: "" });

  const loadDocs = async () => {
    try { const { data } = await api.get("/coach/documents"); setDocs(data); } catch { /* student */ }
  };
  const loadCreds = async () => {
    try { const { data } = await api.get("/coach/credentials"); setCreds(data); } catch { /* student */ }
  };

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setProfile({
        coach_sports: r.data.coach_sports || ["pickleball"],
        headline: r.data.headline || "",
        bio: r.data.bio || "",
        venues: r.data.venues || [],
        hourly_rate: r.data.hourly_rate || 20,
        pricing: {
          group: r.data.pricing?.group ?? 12,
          performance: r.data.pricing?.performance ?? 60,
          one_on_one: r.data.pricing?.one_on_one ?? (r.data.hourly_rate || 40),
        },
      });
      setMyRegions(r.data.regions || []);
      setPendingReqs(r.data.region_requests || []);
    });
    api.get("/regions?active=true").then((r) => setRegions(r.data || [])).catch(() => {});
    api.get("/coach/region-suggestions").then((r) => setMySuggestions(r.data || [])).catch(() => {});
    loadDocs();
    loadCreds();
  }, [user]);

  const reloadRegions = async () => {
    const [r, sg] = await Promise.all([
      api.get("/auth/me"),
      api.get("/coach/region-suggestions").catch(() => ({ data: [] })),
    ]);
    setMyRegions(r.data.regions || []);
    setPendingReqs(r.data.region_requests || []);
    setMySuggestions(sg.data || []);
  };

  const submitSuggestion = async (e) => {
    e?.preventDefault?.();
    const label = suggestForm.suggested_label.trim();
    if (!label) { toast.error("Region name is required"); return; }
    try {
      await api.post("/coach/region-suggestions", {
        suggested_label: label,
        parent_region: suggestForm.parent_region.trim() || null,
        centre_postcode: suggestForm.centre_postcode.trim().toUpperCase() || null,
        notes: suggestForm.notes.trim(),
      });
      toast.success("Suggestion submitted — admin will review");
      setSuggestOpen(false);
      setSuggestForm({ suggested_label: "", parent_region: "", centre_postcode: "", notes: "" });
      await reloadRegions();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const requestRegion = async (rid) => {
    try {
      const { data } = await api.post("/coach/regions", { region_id: rid });
      if (data.status === "approved") toast.success("Region added");
      else if (data.status === "pending") toast.message("Submitted — awaiting admin approval");
      else if (data.status === "already_approved") toast.message("Already in your list");
      else if (data.status === "already_pending") toast.message("Already pending review");
      await reloadRegions();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const dropRegion = async (rid) => {
    if (!window.confirm("Remove this region? Existing offerings stay live but new ones won't show in this region.")) return;
    try {
      await api.delete(`/coach/regions/${rid}`);
      toast.success("Removed");
      await reloadRegions();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleSport = (s) => {
    if (s === "pickleball") return;
    setProfile((p) => ({ ...p, coach_sports: p.coach_sports.includes(s) ? p.coach_sports.filter((x) => x !== s) : [...p.coach_sports, s] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/coach/profile", {
        ...profile,
        venues: profile.venues.filter((v) => v.trim()),
        hourly_rate: Number(profile.hourly_rate) || 0,
        pricing: {
          group: Number(profile.pricing?.group) || 0,
          performance: Number(profile.pricing?.performance) || 0,
          one_on_one: Number(profile.pricing?.one_on_one) || 0,
        },
      });
      toast.success("Profile saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const handleUpload = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/coach/documents?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${kind} uploaded`);
      loadDocs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(null); }
  };

  const removeDoc = async (id) => {
    if (!confirm("Delete this document?")) return;
    try { await api.delete(`/coach/documents/${id}`); toast.success("Removed"); loadDocs(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/coach/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.original_name || d.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const allSports = ["pickleball", ...(enabledSports.includes("squash") ? ["squash"] : [])];

  return (
    <div className="space-y-6" data-testid="coach-profile">
      <div className="flex items-end justify-between flex-wrap gap-4 fade-up">
        <div>
          <div className="label-mono mb-2 flex items-center gap-2"><User className="w-3.5 h-3.5" /> COACH · PROFILE</div>
          <h1 className="h-display text-5xl tracking-tighter">My profile.</h1>
        </div>
        <button onClick={save} disabled={saving} className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2 disabled:opacity-50" data-testid="save-profile">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      {/* Credentials status banner */}
      {creds && (
        <>
          {creds.any_lapsed && (
            <div className="card-surface p-5 fade-up d1 border-l-4" style={{ borderLeftColor: "#FF4500", background: "#FF450010" }} data-testid="lapsed-banner">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#FF4500] shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-[#FF4500]">Insurance lapsed — new offerings are blocked</div>
                  <div className="text-sm text-[#E5E5E5] mt-1">You can&apos;t publish new sessions until you renew your policy. Upload the renewed document below and email admin so they can verify and reinstate publishing.</div>
                </div>
              </div>
            </div>
          )}
          <div className="card-surface p-6 fade-up d1 space-y-4" data-testid="credentials-status">
            <div className="label-mono">Credentials status</div>
            <div className="grid md:grid-cols-2 gap-3">
              {creds.items.map((c) => {
                const meta = STATUS_META[c.status] || STATUS_META.no_expiry;
                const Icon = meta.icon;
                return (
                  <div key={c.kind} className="border p-4" style={{ borderColor: meta.color + "55" }} data-testid={`cred-${c.kind}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <Icon className="w-5 h-5 mt-0.5" style={{ color: meta.color }} strokeWidth={1.8} />
                        <div>
                          <div className="label-mono mb-1" style={{ color: meta.color }}>{c.kind.toUpperCase()}</div>
                          <div className="text-sm font-semibold">{c.provider || "—"}</div>
                          {c.reference && <div className="text-xs text-[#A3A3A3]">{c.reference}</div>}
                        </div>
                      </div>
                      <div className="text-right">
                        {c.expires ? (
                          <>
                            <div className="h-display text-2xl" style={{ color: meta.color }}>
                              {c.lapsed ? `${Math.abs(c.days_until_expiry)}d` : `${c.days_until_expiry}d`}
                            </div>
                            <div className="label-mono text-[0.6rem]">{c.lapsed ? "AGO · LAPSED" : "REMAINING"}</div>
                            <div className="text-[0.65rem] text-[#A3A3A3] mt-0.5">{c.expires}</div>
                          </>
                        ) : (
                          <div className="label-mono text-[#A3A3A3]">NO EXPIRY</div>
                        )}
                      </div>
                    </div>
                    <div className="text-xs mt-2" style={{ color: meta.color }}>{meta.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Public profile fields */}
      <div className="card-surface p-6 fade-up d1 space-y-4">
        <div className="label-mono">Public profile (visible on /coaches)</div>
        <div>
          <div className="label-mono mb-2">Headline</div>
          <input value={profile.headline} onChange={(e) => setProfile({ ...profile, headline: e.target.value })} data-testid="headline"
            placeholder="e.g. PPR-certified pickleball coach · 3.5+ club players welcome"
            className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" />
        </div>
        <div>
          <div className="label-mono mb-2">Bio</div>
          <textarea value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} rows={3} data-testid="bio"
            className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" />
        </div>
        <div>
          <div className="label-mono mb-2 text-[#CCFF00]">YOUR PRICING</div>
          <p className="text-xs text-[#A3A3A3] mb-3">These are the rates players see on your coach profile. Rally Lab&apos;s landing page shows &ldquo;From £...&rdquo; figures based on the floor across all coaches.</p>
          <div className="grid sm:grid-cols-3 gap-3" data-testid="coach-pricing-grid">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1">Group · per player</div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#A3A3A3]">£</span>
                <input type="number" min="0" value={profile.pricing?.group ?? ""}
                  onChange={(e) => setProfile({ ...profile, pricing: { ...profile.pricing, group: e.target.value } })}
                  data-testid="price-group"
                  className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#007AFF]" />
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1">Performance package</div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#A3A3A3]">£</span>
                <input type="number" min="0" value={profile.pricing?.performance ?? ""}
                  onChange={(e) => setProfile({ ...profile, pricing: { ...profile.pricing, performance: e.target.value } })}
                  data-testid="price-performance"
                  className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4500]" />
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1">1:1 · per hour</div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#A3A3A3]">£</span>
                <input type="number" min="0" value={profile.pricing?.one_on_one ?? ""}
                  onChange={(e) => setProfile({ ...profile, pricing: { ...profile.pricing, one_on_one: e.target.value }, hourly_rate: e.target.value })}
                  data-testid="price-one_on_one"
                  className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" />
              </div>
            </div>
          </div>
        </div>
        <div>
          <div className="label-mono mb-2">Venues (one per line)</div>
          <textarea value={(profile.venues || []).join("\n")} onChange={(e) => setProfile({ ...profile, venues: e.target.value.split("\n") })} rows={3} data-testid="venues"
            placeholder="Roko Gym&#10;Westside Courts"
            className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" />
        </div>
      </div>

      {/* Regions / areas — coach picks where they operate. First region auto-approves;
          additional regions go to a pending state until an admin approves them. */}
      <div className="card-surface p-6 fade-up d2 space-y-4" data-testid="coach-regions-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="label-mono">Regions I coach in</div>
          <div className="text-[10px] text-[#525252]">{myRegions.length} approved · {pendingReqs.length} pending</div>
        </div>
        <p className="text-xs text-[#A3A3A3] -mt-2">
          Your offerings only appear in the regions you&apos;re approved for. Your first region auto-approves; additional ones need admin sign-off so coverage stays focused.
        </p>

        {/* Approved regions */}
        <div className="flex flex-wrap gap-2">
          {myRegions.length === 0 && (
            <div className="text-xs text-[#FF4500] italic">No regions yet — pick one below.</div>
          )}
          {myRegions.map((rid) => {
            const r = regions.find((x) => x.id === rid);
            if (!r) return null;
            return (
              <span key={rid} className="inline-flex items-center gap-2 border border-[#CCFF00] text-[#CCFF00] px-3 py-1 text-xs" data-testid={`my-region-${r.slug}`}>
                {r.label}
                <button onClick={() => dropRegion(rid)} className="text-[#A3A3A3] hover:text-[#FF4500]" data-testid={`drop-region-${r.slug}`}>×</button>
              </span>
            );
          })}
          {pendingReqs.map((req) => (
            <span key={req.id} className="inline-flex items-center gap-2 border border-[#FF4500] text-[#FF4500] px-3 py-1 text-xs" data-testid={`pending-region-${req.region_id}`}>
              {req.region_label} <span className="text-[9px] uppercase">pending</span>
            </span>
          ))}
        </div>

        {/* Available regions to request */}
        <div>
          <div className="label-mono mb-2 text-[10px]">Available regions</div>
          <div className="flex flex-wrap gap-2">
            {regions
              .filter((r) => !myRegions.includes(r.id) && !pendingReqs.some((p) => p.region_id === r.id))
              .map((r) => (
                <button
                  key={r.id}
                  onClick={() => requestRegion(r.id)}
                  className="text-xs uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] px-3 py-1.5"
                  data-testid={`request-region-${r.slug}`}
                >
                  + {r.label}
                </button>
              ))}
            {regions.length === 0 && (
              <div className="text-xs text-[#A3A3A3] italic">No regions curated yet — ask an admin to set some up.</div>
            )}
          </div>
        </div>

        {/* Suggest a new region — coach proposes an area Rally Lab doesn't cover yet */}
        <div className="pt-4 border-t border-[#262626]">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div>
              <div className="label-mono text-[10px]">Don&apos;t see your area?</div>
              <div className="text-xs text-[#A3A3A3]">Suggest a new region and admin will review it.</div>
            </div>
            <button
              type="button"
              onClick={() => setSuggestOpen((s) => !s)}
              className="text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black px-3 py-1.5"
              data-testid="toggle-suggest-region"
            >
              {suggestOpen ? "Cancel" : "+ Suggest a region"}
            </button>
          </div>

          {suggestOpen && (
            <form onSubmit={submitSuggestion} className="space-y-3 p-3 border border-[#262626] mt-2" data-testid="suggest-region-form">
              <div>
                <div className="label-mono mb-1 text-[10px]">Region name *</div>
                <input
                  value={suggestForm.suggested_label}
                  onChange={(e) => setSuggestForm({ ...suggestForm, suggested_label: e.target.value })}
                  placeholder="e.g. Snowdonia — Bangor to Betws-y-Coed"
                  className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]"
                  data-testid="suggest-region-label"
                  required
                />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <div className="label-mono mb-1 text-[10px]">Parent (optional)</div>
                  <input
                    value={suggestForm.parent_region}
                    onChange={(e) => setSuggestForm({ ...suggestForm, parent_region: e.target.value })}
                    placeholder="e.g. North Wales"
                    className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]"
                    data-testid="suggest-region-parent"
                  />
                </div>
                <div>
                  <div className="label-mono mb-1 text-[10px]">Centre postcode</div>
                  <input
                    value={suggestForm.centre_postcode}
                    onChange={(e) => setSuggestForm({ ...suggestForm, centre_postcode: e.target.value })}
                    placeholder="e.g. LL57"
                    className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]"
                    data-testid="suggest-region-postcode"
                  />
                </div>
              </div>
              <div>
                <div className="label-mono mb-1 text-[10px]">Notes for admin (optional)</div>
                <textarea
                  value={suggestForm.notes}
                  onChange={(e) => setSuggestForm({ ...suggestForm, notes: e.target.value })}
                  placeholder="Why this area? Venues you already play at, demand you've seen, etc."
                  rows={3}
                  maxLength={500}
                  className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]"
                  data-testid="suggest-region-notes"
                />
              </div>
              <button type="submit" className="btn-volt px-4 py-2 text-xs" data-testid="submit-region-suggestion">
                Submit for review
              </button>
            </form>
          )}

          {mySuggestions.length > 0 && (
            <div className="mt-3 space-y-1" data-testid="my-suggestions-list">
              <div className="label-mono text-[10px] text-[#A3A3A3]">Your suggestions</div>
              {mySuggestions.map((s) => (
                <div key={s.id} className="flex items-center gap-2 text-xs py-1" data-testid={`my-suggestion-${s.id}`}>
                  <span className="text-white">{s.suggested_label}</span>
                  <span
                    className="text-[9px] uppercase tracking-wider px-1.5 py-0.5"
                    style={{
                      color: s.status === "approved" ? "#CCFF00" : s.status === "rejected" ? "#FF4500" : "#A3A3A3",
                      borderColor: s.status === "approved" ? "#CCFF00" : s.status === "rejected" ? "#FF4500" : "#A3A3A3",
                      borderWidth: 1,
                    }}
                  >
                    {s.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sports */}
      <div className="card-surface p-6 fade-up d2 space-y-4">
        <div className="label-mono">Sports I teach</div>
        <div className="grid md:grid-cols-2 gap-3">
          {allSports.map((s) => {
            const m = SPORT_META[s];
            const on = (profile.coach_sports || []).includes(s);
            const locked = s === "pickleball";
            return (
              <button key={s} onClick={() => toggleSport(s)} disabled={locked} data-testid={`profile-sport-${s}`}
                className="p-4 border text-left"
                style={on ? { borderColor: m.color, background: m.color + "10" } : { borderColor: "#262626" }}>
                <div className="flex items-center justify-between mb-1">
                  <span className="label-mono" style={{ color: m.color }}>{m.label.toUpperCase()}</span>
                  <span className="text-xs" style={{ color: on ? m.color : "#A3A3A3" }}>{locked ? "Always on" : on ? "Enabled" : "Off"}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Documents — private */}
      <div className="card-surface p-6 fade-up d3 space-y-4">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#FF4500]" />
          <div className="label-mono">Documents · Private (only you &amp; admin)</div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          {["certification", "insurance"].map((kind) => (
            <label key={kind} className="border border-[#262626] p-5 hover:border-[#CCFF00] transition-colors cursor-pointer block" data-testid={`upload-zone-${kind}`}>
              <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,.heic" className="hidden"
                onChange={(e) => handleUpload(kind, e.target.files[0])} data-testid={`upload-${kind}`} />
              <Upload className="w-6 h-6 text-[#CCFF00] mb-2" strokeWidth={1.5} />
              <div className="font-semibold text-sm capitalize">{uploading === kind ? "Uploading…" : `Upload ${kind} image / PDF`}</div>
              <div className="text-xs text-[#A3A3A3] mt-1">JPG, PNG, WebP, HEIC, or PDF. Max 8MB. Visible only to you and Rally Lab admins.</div>
            </label>
          ))}
        </div>

        {docs.length > 0 && (
          <div className="space-y-2 pt-2">
            <div className="label-mono">Your uploaded documents</div>
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 py-2 border-b border-[#262626] last:border-0" data-testid={`doc-${d.id}`}>
                <FileText className="w-4 h-4 text-[#A3A3A3]" />
                <div className="flex-1">
                  <div className="text-sm font-semibold capitalize">{d.kind} · <span className="text-[#A3A3A3] font-normal text-xs">{d.original_name}</span></div>
                  <div className="text-[0.65rem] text-[#A3A3A3] label-mono">{(d.size/1024).toFixed(0)} KB · uploaded {d.uploaded_at?.slice(0,10)}</div>
                </div>
                <button onClick={() => download(d)} className="text-xs uppercase tracking-wider text-[#CCFF00] hover:underline inline-flex items-center gap-1" data-testid={`download-${d.id}`}>
                  <Download className="w-3.5 h-3.5" /> Download
                </button>
                <button onClick={() => removeDoc(d.id)} className="text-[#FF4500] hover:opacity-70" data-testid={`remove-${d.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Iter99 — Team Access. Grant/revoke other coaches visibility on my
          students so they can co-coach and record assessments as themselves.
          Kept at the bottom of the profile because it's a rare-touch setting
          but important when a new coach joins the team. */}
      <TeamAccessSection />

      {/* Iter105 (Slice C) — Away Mode + Directory Visibility. Coach-controlled
          switches that gate the Book button on the public directory without
          removing them entirely. Away is temporary, Visible is a hard hide. */}
      <AvailabilitySection />

      {/* Iter119 — Coach flat group rate for group-booking pricing. */}
      <GroupRateSection />

      <VenueRequirementSection />

      {/* Iter121 — Simplified Mode toggle + tile picker. */}
      <SimplifiedModeSection />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Iter105 (Slice C) — Coach availability controls.
// ---------------------------------------------------------------------------
function AvailabilitySection() {
  const [away, setAway] = useState(false);
  const [awayNote, setAwayNote] = useState("");
  const [visible, setVisible] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setAway(!!r.data.is_away);
      setAwayNote(r.data.away_note || "");
      setVisible(r.data.is_visible_to_public !== false);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const toggleAway = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/coach/away-mode", { away: next, note: awayNote });
      setAway(!!data.is_away);
      toast.success(next ? "Away mode ON — Book buttons paused" : "Away mode OFF — you're bookable again");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setSaving(false); }
  };

  const saveNote = async () => {
    setSaving(true);
    try {
      await api.put("/me/coach/away-mode", { away, note: awayNote });
      toast.success("Away note saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setSaving(false); }
  };

  const toggleVisible = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/coach/visibility", { visible: next });
      setVisible(!!data.is_visible_to_public);
      toast.success(next ? "Now visible in the directory" : "Hidden from the public directory");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setSaving(false); }
  };

  if (!loaded) return null;

  return (
    <div className="card-surface p-6 fade-up d3 space-y-5" data-testid="availability-section">
      <div>
        <div className="label-mono flex items-center gap-2"><Coffee className="w-3.5 h-3.5" /> AVAILABILITY</div>
        <h2 className="h-display text-2xl mt-1">Pause bookings or hide from the directory.</h2>
        <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl leading-relaxed">
          Away mode keeps you in the directory but marks you as <span className="text-white">Currently Away</span> and disables the Book buttons. Hiding removes you entirely — use it for extended breaks or when your profile isn&apos;t ready to be public.
        </p>
      </div>

      {/* Away Mode */}
      <div className="border border-[#262626] p-4 space-y-3" data-testid="away-mode-card">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white flex items-center gap-2">
              <Coffee className="w-4 h-4 text-[#FF9F1C]" /> Away Mode
              {away && <span className="ml-2 px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#FF9F1C] text-black font-bold">Active</span>}
            </div>
            <div className="text-xs text-[#A3A3A3] mt-1">Stay listed with a badge, but pause new bookings.</div>
          </div>
          <button
            type="button"
            onClick={() => toggleAway(!away)}
            disabled={saving}
            data-testid="away-mode-toggle"
            className="text-xs uppercase tracking-wider px-4 py-2 font-semibold disabled:opacity-50"
            style={away
              ? { background: "#FF9F1C", color: "#0A0A0A" }
              : { background: "transparent", color: "#FF9F1C", border: "1px solid #FF9F1C" }}
          >
            {away ? "Turn OFF" : "Turn ON"}
          </button>
        </div>
        <div>
          <div className="label-mono text-[10px] mb-1">Short note (optional, shown in badge tooltip · 200 char max)</div>
          <div className="flex gap-2">
            <input
              value={awayNote}
              maxLength={200}
              onChange={(e) => setAwayNote(e.target.value)}
              placeholder="Back from 14 Feb — travelling"
              data-testid="away-mode-note"
              className="flex-1 bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#FF9F1C]"
            />
            <button
              type="button"
              onClick={saveNote}
              disabled={saving}
              data-testid="away-mode-save-note"
              className="text-xs uppercase tracking-wider px-3 py-2 border border-[#262626] hover:border-[#FF9F1C] hover:text-[#FF9F1C] disabled:opacity-50"
            >
              Save note
            </button>
          </div>
        </div>
      </div>

      {/* Directory visibility */}
      <div className="border border-[#262626] p-4" data-testid="visibility-card">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white flex items-center gap-2">
              {visible ? <Eye className="w-4 h-4 text-[#CCFF00]" /> : <EyeOff className="w-4 h-4 text-[#FF4500]" />}
              Directory visibility
              <span
                className="ml-2 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold"
                style={visible
                  ? { background: "#CCFF00", color: "#0A0A0A" }
                  : { background: "#FF4500", color: "#0A0A0A" }}
              >
                {visible ? "Visible" : "Hidden"}
              </span>
            </div>
            <div className="text-xs text-[#A3A3A3] mt-1">
              {visible
                ? "You appear on /coaches, in Coaches Near Me, and in region avatars."
                : "You're hidden from public lists. Existing students still see you."}
            </div>
          </div>
          <button
            type="button"
            onClick={() => toggleVisible(!visible)}
            disabled={saving}
            data-testid="visibility-toggle"
            className="text-xs uppercase tracking-wider px-4 py-2 font-semibold disabled:opacity-50"
            style={visible
              ? { background: "transparent", color: "#FF4500", border: "1px solid #FF4500" }
              : { background: "#CCFF00", color: "#0A0A0A" }}
          >
            {visible ? "Hide me" : "Show me"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Iter99 — TeamAccessSection: one-way share of student access.
// ---------------------------------------------------------------------------
function TeamAccessSection() {
  const [summary, setSummary] = useState({ grants_out: [], grants_in: [] });
  const [candidates, setCandidates] = useState([]);
  const [picking, setPicking] = useState(false);
  const [pickedId, setPickedId] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, c] = await Promise.all([
        api.get("/coach/team-access"),
        api.get("/coach/team-access/candidates"),
      ]);
      setSummary(s.data || { grants_out: [], grants_in: [] });
      setCandidates(Array.isArray(c.data) ? c.data : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load team access");
    }
  };
  useEffect(() => { load(); }, []);

  const grantedIds = new Set((summary.grants_out || []).map((g) => g.coach_id));
  const availableCandidates = candidates.filter((c) => !grantedIds.has(c.id));

  const grant = async () => {
    if (!pickedId) return;
    setSaving(true);
    try {
      await api.post("/coach/team-access", { grantee_coach_id: pickedId, enabled: true });
      const c = candidates.find((x) => x.id === pickedId);
      toast.success(`${c?.name || "Coach"} can now see your students`);
      setPickedId("");
      setPicking(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Grant failed");
    } finally { setSaving(false); }
  };
  const revoke = async (g) => {
    if (!window.confirm(`Revoke ${g.name}'s access to your students?\n\nThey will no longer see your roster or be able to record assessments.`)) return;
    try {
      await api.post("/coach/team-access", { grantee_coach_id: g.coach_id, enabled: false });
      toast.success(`${g.name} access revoked`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Revoke failed");
    }
  };

  return (
    <div className="card-surface p-6 fade-up d3 space-y-4" data-testid="team-access-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-mono flex items-center gap-2"><Users className="w-3.5 h-3.5" /> TEAM ACCESS</div>
          <h2 className="h-display text-2xl mt-1">Share my students with team coaches.</h2>
          <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl leading-relaxed">
            Tick a coach and they can see your entire roster, book with them, and record
            assessments as themselves — every entry logs their name in the audit trail,
            not yours. Sharing is <span className="text-white">one-way</span>: they&apos;d need to
            grant you access separately.
          </p>
        </div>
        {!picking && availableCandidates.length > 0 && (
          <button
            type="button"
            onClick={() => setPicking(true)}
            className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-2"
            data-testid="team-access-add"
          >
            <UserPlus className="w-4 h-4" /> Share with a coach
          </button>
        )}
      </div>

      {picking && (
        <div className="border border-[#262626] p-4 space-y-3" data-testid="team-access-picker">
          <div className="label-mono">Pick a coach to grant access</div>
          <select
            value={pickedId}
            onChange={(e) => setPickedId(e.target.value)}
            className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-sm focus:border-[#CCFF00] focus:outline-none"
            data-testid="team-access-pick-select"
          >
            <option value="">— select coach —</option>
            {availableCandidates.map((c) => (
              <option key={c.id} value={c.id}>{c.name} · {c.email}</option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={grant}
              disabled={!pickedId || saving}
              className="btn-volt px-3 py-2 text-xs disabled:opacity-50"
              data-testid="team-access-grant"
            >
              {saving ? "Sharing…" : "Grant access"}
            </button>
            <button
              type="button"
              onClick={() => { setPicking(false); setPickedId(""); }}
              className="text-xs px-3 py-2 border border-[#262626] text-[#A3A3A3] hover:text-white"
              data-testid="team-access-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Coaches I've granted */}
      <div>
        <div className="label-mono mb-2">You share your students with</div>
        {(summary.grants_out || []).length === 0 ? (
          <div className="text-xs text-[#525252] italic border border-dashed border-[#262626] p-3" data-testid="team-access-out-empty">
            Nobody yet. Add a co-coach above to let them see your roster.
          </div>
        ) : (
          <div className="space-y-2" data-testid="team-access-out-list">
            {summary.grants_out.map((g) => (
              <div key={g.coach_id} className="flex items-center gap-3 border border-[#262626] p-2.5" data-testid={`team-access-out-${g.coach_id}`}>
                <div className="w-8 h-8 rounded-full bg-[#141414] flex items-center justify-center text-xs text-[#CCFF00] font-mono shrink-0">
                  {(g.name || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{g.name}</div>
                  <div className="text-[10px] text-[#A3A3A3] truncate">{g.email}</div>
                </div>
                <button
                  type="button"
                  onClick={() => revoke(g)}
                  className="text-xs text-[#FF4500] hover:opacity-70 inline-flex items-center gap-1"
                  data-testid={`team-access-revoke-${g.coach_id}`}
                >
                  <X className="w-3.5 h-3.5" /> Revoke
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Coaches who've granted me access */}
      {(summary.grants_in || []).length > 0 && (
        <div>
          <div className="label-mono mb-2">You can see students from</div>
          <div className="space-y-2" data-testid="team-access-in-list">
            {summary.grants_in.map((g) => (
              <div key={g.coach_id} className="flex items-center gap-3 border border-[#262626] p-2.5" data-testid={`team-access-in-${g.coach_id}`}>
                <div className="w-8 h-8 rounded-full bg-[#141414] flex items-center justify-center text-xs text-[#CCFF00] font-mono shrink-0">
                  {(g.name || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{g.name}</div>
                  <div className="text-[10px] text-[#A3A3A3] truncate">{g.email}</div>
                </div>
                <span className="text-[10px] text-[#CCFF00] uppercase tracking-widest">Sharing</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Iter119 — Coach flat group rate for group-booking pricing.
// ---------------------------------------------------------------------------
function GroupRateSection() {
  const [rate, setRate] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setRate(r.data.group_session_rate ?? "");
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    const v = Number(rate);
    if (isNaN(v) || v < 0) { toast.error("Enter a valid positive number"); return; }
    setBusy(true);
    try {
      await api.put("/me/coach/group-rate", { group_session_rate: v });
      toast.success(`Group rate saved · £${v}/hour`);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  // Iter120 — Onboarding nudge: coaches without a rate set see an amber
  // prompt so they realise they can't receive group booking requests until
  // this field is populated. Dismisses automatically once rate > 0 saved.
  const showNudge = loaded && (rate === "" || Number(rate) === 0);

  return (
    <div className="card-surface p-6 fade-up d4 space-y-3" data-testid="group-rate-section">
      <div className="label-mono">GROUP SESSION RATE</div>
      <h2 className="h-display text-2xl">Flat £/hour for group bookings.</h2>
      {showNudge && (
        <div className="border-l-2 border-[#FF9F1C] bg-[#141414] px-3 py-2 text-[12px] text-white flex items-center gap-2" data-testid="group-rate-nudge">
          <span className="text-[#FF9F1C] font-semibold uppercase tracking-wider text-[10px]">Set your rate</span>
          <span className="text-[#A3A3A3]">— you can't receive group booking requests until this is set.</span>
        </div>
      )}
      <p className="text-sm text-[#A3A3A3] max-w-2xl leading-relaxed">
        Used to price group booking requests when students organise their own sessions. Total shown to them = <span className="text-white">rate × duration / 60</span>. You can override on approval.
      </p>
      <div className="flex items-center gap-2">
        <span className="text-white text-lg">£</span>
        <input
          type="number" min={0} step={5} value={rate}
          onChange={(e) => setRate(e.target.value)}
          data-testid="group-rate-input"
          className="w-32 bg-[#141414] border border-[#262626] px-3 py-2 text-white focus:outline-none focus:border-[#CCFF00]"
        />
        <span className="text-[#A3A3A3] text-sm">per hour</span>
        <button type="button" onClick={save} disabled={busy}
          data-testid="group-rate-save"
          className="ml-2 px-4 py-2 text-xs uppercase tracking-wider bg-[#CCFF00] text-black font-bold disabled:opacity-50">
          {busy ? "Saving…" : saved ? "Saved" : "Save"}
        </button>
      </div>
    </div>
  );
}

function VenueRequirementSection() {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/auth/me").then((r) => setOn(!!r.data.student_arranges_venue)).catch(() => {});
  }, []);
  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/me/coach/venue-requirement", { required: !on });
      setOn(data.student_arranges_venue);
      toast.success(data.student_arranges_venue ? "Students will be told to book & pay for the venue." : "Venue requirement removed.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="card-surface p-6 fade-up d4 space-y-3" data-testid="venue-requirement-section">
      <div className="label-mono">VENUE &amp; COURT FEES</div>
      <h2 className="h-display text-2xl">Students organise the venue?</h2>
      <p className="text-sm text-[#A3A3A3] max-w-2xl leading-relaxed">
        When on, students requesting a tailored session with you see: <span className="text-white">"Your coach requires you to organise and pay for the venue directly."</span>
      </p>
      <button type="button" onClick={toggle} disabled={busy} data-testid="venue-requirement-toggle" data-state={on ? "on" : "off"}
        className="px-4 py-2 text-xs uppercase tracking-wider font-bold border transition-colors disabled:opacity-50"
        style={on ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00" } : { borderColor: "#262626", color: "#A3A3A3" }}>
        {on ? "On — students arrange venue" : "Off — I arrange the venue"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Iter121 — Simplified Mode: coach picks whether the dashboard shows a
// large-tile launcher and which tiles appear (up to 8). Persisted server-side
// so the choice sticks across devices.
// ---------------------------------------------------------------------------
const TILE_OPTIONS = [
  { key: "today",          label: "Today's Sessions" },
  { key: "students",       label: "My Students" },
  { key: "feedback_needed",label: "Feedback Needed" },
  { key: "assessments",    label: "Assessments" },
  { key: "availability",   label: "Set Availability" },
  { key: "offerings",      label: "Offerings" },
  { key: "messages",       label: "Messages" },
  { key: "club_requests",  label: "Session Requests" },
  { key: "group_requests", label: "Legacy Requests" },
  { key: "pricing",        label: "Pricing" },
  { key: "festival",       label: "Festivals" },
  { key: "admin",          label: "Admin Console", adminOnly: true },
  { key: "stats",          label: "Stats & KPIs", adminOnly: true },
];
const DEFAULT_TILES = ["today", "students", "feedback_needed", "assessments", "availability", "club_requests", "festival"];
const MAX_TILES = 8;

function SimplifiedModeSection() {
  const { user, setUser } = useAuth();
  const [enabled, setEnabled] = useState(true);
  const [selected, setSelected] = useState(DEFAULT_TILES);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const perms = new Set(user?.permissions || []);
  if (user?.role === "admin") perms.add("admin");
  const isAdmin = perms.has("admin") || perms.has("super_admin");
  const visibleOptions = TILE_OPTIONS.filter((t) => (t.adminOnly ? isAdmin : true));

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      // Default ON for new coaches — matches product spec.
      setEnabled(r.data.simplified_mode !== false);
      const tiles = Array.isArray(r.data.simplified_tiles) && r.data.simplified_tiles.length > 0
        ? r.data.simplified_tiles : DEFAULT_TILES;
      setSelected(tiles);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  // Anchor scroll — Coach launcher's "Customise tiles" link jumps here.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash === "#simplified-mode") {
      const el = document.getElementById("simplified-mode");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loaded]);

  const toggle = (key) => {
    setSelected((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= MAX_TILES) {
        toast.error(`Pick up to ${MAX_TILES} tiles.`);
        return prev;
      }
      return [...prev, key];
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/coach/simplified-mode", {
        enabled,
        tiles: selected,
      });
      toast.success(enabled ? "Simplified Mode ON — launcher will show next visit." : "Full Dashboard restored.");
      if (setUser && user) {
        setUser({
          ...user,
          simplified_mode: !!data.simplified_mode,
          simplified_tiles: data.simplified_tiles || selected,
        });
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!loaded) return null;

  return (
    <div id="simplified-mode" className="card-surface p-6 fade-up d5 space-y-5" data-testid="simplified-mode-section">
      <div>
        <div className="label-mono flex items-center gap-2"><Layout className="w-3.5 h-3.5" /> SIMPLIFIED MODE</div>
        <h2 className="h-display text-2xl mt-1">Big tiles instead of the full console.</h2>
        <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl leading-relaxed">
          When on, your dashboard opens straight into a tile grid. Pick up to <span className="text-white">{MAX_TILES} tiles</span> — Today's Sessions, My Students, Assessments and Set Availability are on by default.
        </p>
      </div>

      {/* Master toggle */}
      <div className="border border-[#262626] p-4" data-testid="simplified-mode-toggle-card">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white flex items-center gap-2">
              <Layout className="w-4 h-4 text-[#CCFF00]" /> Simplified Mode
              <span
                className="ml-2 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold"
                style={enabled
                  ? { background: "#CCFF00", color: "#0A0A0A" }
                  : { background: "#262626", color: "#A3A3A3" }}
              >
                {enabled ? "ON" : "OFF"}
              </span>
            </div>
            <div className="text-xs text-[#A3A3A3] mt-1">
              {enabled ? "Dashboard opens with tile grid." : "Dashboard opens with full console."}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setEnabled((v) => !v)}
            data-testid="simplified-mode-toggle"
            className="text-xs uppercase tracking-wider px-4 py-2 font-semibold"
            style={enabled
              ? { background: "transparent", color: "#FF4500", border: "1px solid #FF4500" }
              : { background: "#CCFF00", color: "#0A0A0A" }}
          >
            {enabled ? "Turn OFF" : "Turn ON"}
          </button>
        </div>
      </div>

      {/* Tile picker */}
      <div className="border border-[#262626] p-4 space-y-3" data-testid="simplified-mode-tiles">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="label-mono">Pick your tiles ({selected.length}/{MAX_TILES})</div>
          <button
            type="button"
            onClick={() => setSelected(DEFAULT_TILES)}
            data-testid="simplified-mode-reset"
            className="text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-white"
          >
            Reset to defaults
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {visibleOptions.map((t) => {
            const on = selected.includes(t.key);
            return (
              <button
                type="button"
                key={t.key}
                onClick={() => toggle(t.key)}
                data-testid={`tile-option-${t.key}`}
                className="flex items-center gap-2 border px-3 py-2 text-left text-xs transition-colors"
                style={on
                  ? { borderColor: "#CCFF00", background: "#CCFF0010", color: "#fff" }
                  : { borderColor: "#262626", color: "#A3A3A3" }}
              >
                <span className="w-4 h-4 border shrink-0 inline-flex items-center justify-center"
                  style={on ? { borderColor: "#CCFF00", background: "#CCFF00" } : { borderColor: "#525252" }}>
                  {on && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                </span>
                <span className="truncate">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="simplified-mode-save"
          className="btn-volt px-5 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save preferences"}
        </button>
      </div>
    </div>
  );
}
